package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.PluralCategory
import dev.lingvo.site.ds.Space
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * Every plural form the target locale needs, as a stack of labelled fields.
 *
 * **This was a tab strip, and the tab strip was the bug.** `docs/DESIGN.md` is specific — "the
 * target field becomes *a set of labelled fields* — one per CLDR category that the target locale
 * actually uses (Japanese shows one field, Russian shows four, Arabic shows six)" — and it is
 * specific for a reason a general-purpose tab component cannot know: **plural forms are written in
 * relation to each other.** товар / товара / товаров differ by a suffix, and a translator keeping
 * them consistent has to see them together. Behind tabs, three of Russian's four forms were always
 * hidden, and the only way to compare two was to remember one.
 *
 * The same applies to what the spec calls marking empty categories inline: a required form with
 * nothing in it is invisible when it sits behind an unselected tab, which is exactly when it most
 * needs to be seen.
 *
 * [selected] is no longer a visibility switch — every form renders — but it is still meaningful:
 * it is *the form the caret is in*, which is what `activeEditorTarget` builds a focus ref from and
 * what an advancing save restores. So it follows focus rather than a click on a tab.
 */
@Composable
internal fun EditorPluralUnit(
    categories: List<PluralCategory>,
    selected: PluralCategory,
    onSelected: (PluralCategory) -> Unit,
    content: @Composable (PluralCategory) -> Unit,
) {
    require(categories.isNotEmpty()) { "EditorPluralUnit requires at least one category" }
    Column(
        Modifier
            .fillMaxWidth()
            .minWidth(0.px)
            // Space.MD, not XXL: DESIGN.md's whole argument for fields over tabs is that товар /
            // товара / товаров "differ by a suffix" and have to be compared at a glance. A 24px
            // gap was appropriate between ~421px bordered cards; now that each form is a compact
            // row, the same gap would spend proportionally more of the screen on space between
            // forms than on the forms themselves.
            .gap(Space.MD)
            .attr("data-editor-plural-forms", "true"),
    ) {
        categories.forEach { category ->
            Div(
                EditorPluralPanelStyle.toModifier()
                    .fillMaxWidth()
                    .minWidth(0.px)
                    .toAttrs {
                        attr("data-editor-plural-panel", "true")
                        attr("data-editor-plural-form", category.name)
                        if (category == selected) attr("data-editor-plural-active", "true")
                        // The caret decides which form is "current". Nothing else can: with every
                        // field on screen at once there is no selection gesture left to read.
                        onFocusIn { onSelected(category) }
                    },
            ) {
                content(category)
            }
        }
    }
}

/** Kept as a named extension so the focus wiring above reads as one idea rather than two. */
private inline fun AttrsScope<*>.onFocusIn(crossinline handler: () -> Unit) {
    addEventListener("focusin") { handler() }
}
